import classNames from "classnames";
import React, { FC, useEffect, useRef, useState } from "react";
import classes from "./ModalWorkplace.module.scss";
import modalBase from "../../../ModalBase/ModalBase.module.scss";
import { CustomTextField } from "../../CustomTextField/CustomTextField";
import { SearchTextField } from "../../SearchTextField/SearchTextField";
import { crossIcon } from "../../../../assets/icons";
import * as yup from "yup";
import { SubmitHandler, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { workingPlaceType } from "../../Tables/WorkingPlacesTable/WorkingPlacesTable";
import { DropDown } from "../../DropDown/DropDown";
import {
  UsersRolesType,
  useGetAllRoles,
} from "../../../../hooks/Admin/WorkingPlacesTable/useGetRolesAndEmployees";
import {
  AssignedEmployeeType,
  WorkPlaceType,
} from "../../../../pagesAdmin/WorkplacesPage/WorkplacesPage";
import { useEditWorkingPlace } from "../../../../hooks/Admin/WorkingPlacesTable/useEditWorkingPlace";
import { useCreateWorkingPlace } from "../../../../hooks/Admin/WorkingPlacesTable/useCreateWorkingPlace";
import { useGetQueuesForCurrentUser } from "../../../../hooks/Admin/WorkingPlacesTable/useGetQueuesForCurrentUser";
import { Button } from "../../Button/Button";
import BranchQueueTreeSelect from "../../BranchQueueTreeSelect/BranchQueueTreeSelect";
import { ModalWorkpaceSchema } from "../../../../schemas/ModalWorkpaceSchema";
import { getUserData } from "../../../../utils/Authorization/authorization";
import type { AxiosError } from "axios";
import { useAdminQueueIdsFilter } from "../../../../hooks/Admin/useAdminQueueIdsFilter";

type ModalWorkplaceProps = {
  isActive: boolean;
  setActive: (value: boolean) => void;
  data?: workingPlaceType;
  allWorkingPlaces: workingPlaceType[];
  setActiveRowData: (value: null | workingPlaceType) => void;
  setAgentsRequestData: (requestBody: WorkPlaceType | null) => void;
  setModalAccessLevelsActive: (modalStatus: boolean) => void;
  agentsRequestData: WorkPlaceType | null;
  backStage: boolean;
  setBackStage: (status: boolean) => void;
  isCreateMode?: boolean;
  setIsCreateMode: (status: boolean) => void;
};

export const ModalWorkplace: FC<ModalWorkplaceProps> = ({
  isActive,
  setActive,
  data,
  allWorkingPlaces,
  setActiveRowData,
  setModalAccessLevelsActive,
  setAgentsRequestData,
  setBackStage,
  isCreateMode,
  setIsCreateMode,
  agentsRequestData,
  backStage,
}) => {
  const [filteredItems, setFilteredItems] = useState<AssignedEmployeeType[]>(
    [],
  );
  const [addedEmployees, setAddedEmployees] = useState<AssignedEmployeeType[]>(
    [],
  );
  const [uniqRoles, setUniqRoles] = useState<UsersRolesType[]>([]);
  const { rolesResp } = useGetAllRoles();
  const [searchText, setSearchText] = useState<string>("");
  const { editWorkingPlace, isLoadingEditWorkingPlace } = useEditWorkingPlace(
    setActive,
    setAgentsRequestData,
  );
  const { createWorkingPlace, isLoadingCreateWorkingPlace } =
    useCreateWorkingPlace(setActive, setAgentsRequestData);

  const [isMenuOpen, setMenuOpen] = useState(false);
  const [dropDownItem, setDropDownItem] = useState(null);
  const [selectedBranchIds, setSelectedBranchIds] = useState<number[]>([]);
  const [selectedQueueIds, setSelectedQueueIds] = useState<number[]>([]);
  const [
    selectedBranchesAndQueuesValidationError,
    setSelectedBranchesAndQueuesValidationError,
  ] = useState<string | null>(null);
  const {
    queues: availableQueues,
    isError: isQueuesError,
    error: queuesError,
    refetch: refetchQueues,
  } = useGetQueuesForCurrentUser();
  const [queuesAccessError, setQueuesAccessError] = useState<string | null>(
    null,
  );
  const [addedEmployeesValidationError, setAddedEmployeesValidationError] =
    useState<string | null>(null);

  const modalRef = useRef<HTMLDivElement>(null);

  type FormData = yup.InferType<typeof ModalWorkpaceSchema>;
  const {
    register,
    watch,
    handleSubmit,
    clearErrors,
    setError,
    setValue,
    reset,
    formState: { errors },
  } = useForm<FormData>({
    resolver: yupResolver(ModalWorkpaceSchema),
    mode: "onChange",
    defaultValues: {
      workpaceName: "",
      boardNumber: "",
      officeNumber: "",
      type: "",
      digitalDisplayIp: "",
      queueName: "",
      searchInputText: "",
    },
  });

  const OPERATOR_ROLES = ["Оператор"] as const;
  const SUPER_ADMIN_TEHNOSKY_ROLE = "Суперадминистратор Tehnosky";

  const { queueIdsFilter } = useAdminQueueIdsFilter();
  const userRole = ((getUserData() as { role?: string })?.role ?? "").trim();
  const isBranchAdmin = userRole.startsWith("Администратор");

  useEffect(() => {
    if (rolesResp) {
      const uniqRolesArr: UsersRolesType[] = rolesResp.reduce(
        (acc, item) =>
          acc.find((e) => e.role === item.role) ? acc : [...acc, item],
        [] as UsersRolesType[],
      );
      const roleNames = uniqRolesArr.map((r) => r.role);
      // Всегда добавляем типы операторов в список, если их ещё нет (для любого администратора)
      OPERATOR_ROLES.forEach((role, index) => {
        if (!roleNames.includes(role)) {
          uniqRolesArr.push({ id: -(index + 1), name: "", role });
        }
      });
      if (isBranchAdmin && !roleNames.includes("Администратор ")) {
        uniqRolesArr.push({ id: -10, name: "", role: "Администратор " });
      }
      // В окне "Создание рабочего места" всегда разрешаем выбирать только уровень доступа "Оператор"
      const rolesToShow = isCreateMode
        ? uniqRolesArr.filter((r) => r.role === "Оператор")
        : uniqRolesArr.filter((r) => r.role !== SUPER_ADMIN_TEHNOSKY_ROLE);
      setUniqRoles(rolesToShow);
    }
  }, [rolesResp, isBranchAdmin, isCreateMode]);

  useEffect(() => {
    if (rolesResp) {
      // Пока не выбраны и филиал, и очередь, список "Добавить сотрудников"
      // должен оставаться пустым (сотрудник привязывается к конкретной связке).
      if (
        selectedBranchIds.length === 0 ||
        selectedQueueIds.length === 0
      ) {
        setFilteredItems([]);
        return;
      }

      const currentUsers = rolesResp
        .filter((user) => user.role === dropDownItem)
        .filter(
          (user) => !addedEmployees?.map((item) => item.id).includes(user.id),
        )
        .filter((user) => {
          const branchMatch =
            selectedBranchIds.length === 0 ||
            (user.branchIds?.length &&
              user.branchIds.some((b) => selectedBranchIds.includes(b)));
          const queueMatch =
            selectedQueueIds.length === 0 ||
            (user.queueIds?.length &&
              selectedQueueIds.every((q) => user.queueIds.includes(q)));
          return Boolean(branchMatch && queueMatch);
        });
      setFilteredItems(currentUsers);
    }
  }, [
    dropDownItem,
    rolesResp,
    addedEmployees,
    selectedBranchIds,
    selectedQueueIds,
  ]);

  useEffect(() => {
    if (!rolesResp) return;

    setAddedEmployees((prevState) => {
      const nextState = prevState.filter((employee) => {
        const userWithBranches = rolesResp.find(
          (user) => user.id === employee.id,
        );
        const branchMatch =
          selectedBranchIds.length > 0 &&
          userWithBranches?.branchIds?.some((branchId) =>
            selectedBranchIds.includes(branchId),
          );
        const queueMatch =
          selectedQueueIds.length > 0 &&
          selectedQueueIds.every((queueId) =>
            userWithBranches?.queueIds?.includes(queueId),
          );
        return Boolean(branchMatch && queueMatch);
      });

      if (nextState.length === prevState.length) {
        return prevState;
      }

      return nextState;
    });
  }, [selectedBranchIds, selectedQueueIds, rolesResp]);

  useEffect(() => {
    if (data?.branches?.length)
      setSelectedBranchIds(data.branches.map((b) => b.id));
    if (data?.queues?.length) setSelectedQueueIds(data.queues.map((q) => q.id));
  }, [data?.id]);

  useEffect(() => {
    if (!isActive) {
      reset();
      setFilteredItems([]);
      setAddedEmployees([]);
      setDropDownItem(null);
      setSelectedBranchIds([]);
      setSelectedQueueIds([]);
      setSelectedBranchesAndQueuesValidationError(null);
      setAddedEmployeesValidationError(null);
    }
  }, [isActive]);

  useEffect(() => {
    if (addedEmployees.length > 0 && addedEmployeesValidationError) {
      setAddedEmployeesValidationError(null);
    }
  }, [addedEmployees, addedEmployeesValidationError]);

  useEffect(() => {
    if (isCreateMode) {
      setValue("workpaceName", "");
      setValue("boardNumber", "");
      setValue("digitalDisplayIp", "");
      setValue("officeNumber", "");
      setAddedEmployees([]);
      // В режиме создания доступен только уровень доступа "Оператор" (предвыбран).
      setDropDownItem("Оператор");
      setValue("type", "Оператор");
    }
    if (backStage) {
      agentsRequestData.description &&
        setValue("workpaceName", agentsRequestData.description);
      agentsRequestData.windowNumber &&
        setValue("boardNumber", agentsRequestData.windowNumber.toString());
      agentsRequestData.digitalDisplayIp &&
        setValue("digitalDisplayIp", agentsRequestData.digitalDisplayIp);
      agentsRequestData.officeNumber &&
        setValue("officeNumber", String(agentsRequestData.officeNumber));
      setAddedEmployees(
        agentsRequestData.assignedEmployees
          ? agentsRequestData.assignedEmployees
          : [],
      );
      setDropDownItem(agentsRequestData.type);
      setValue("type", agentsRequestData.type);
    }
    if (data && !backStage) {
      data.workingPlace && setValue("workpaceName", data.workingPlace);
      data.number && setValue("boardNumber", data.number.toString());
      data.boardIp && setValue("digitalDisplayIp", data.boardIp);
      data.cabinet && setValue("officeNumber", String(data.cabinet));
      setAddedEmployees(data.assignedEmployees ? data.assignedEmployees : []);
      setDropDownItem(data.type);
      setValue("type", data.type);
    }
  }, [data, agentsRequestData, isCreateMode, isBranchAdmin]);

  const addEmloyeeToArr = (item: AssignedEmployeeType) => {
    if (!addedEmployees?.some((service) => service.id === item.id)) {
      setAddedEmployees((prevState) => [...prevState, item]);
    }
  };

  const addRemovedEmployee = (item) => {
    // Возвращаем сотрудника в список "Добавить сотрудников" только когда
    // уже выбраны филиал и очередь (иначе список должен быть пустым).
    if (
      selectedBranchIds.length === 0 ||
      selectedQueueIds.length === 0
    ) {
      return;
    }

    setFilteredItems((prevState) => [...prevState, item]);
  };

  const removeEmplyeeById = (id: number): void => {
    setAddedEmployees((prevState) =>
      prevState.filter((employee: AssignedEmployeeType) => employee.id !== id),
    );
  };

  const removeSelectedUser = (user) => {
    setFilteredItems((prevState) =>
      prevState.filter((item) => item.id !== user.id),
    );
  };

  const onSubmit: SubmitHandler<FormData> = (body: FormData) => {
    if (isQueuesError) {
      const status = (queuesError as AxiosError | undefined)?.response?.status;
      if (status === 403) {
        setQueuesAccessError(
          "Нет доступа к списку очередей (403). Проверьте права пользователя или войдите под администратором филиала/администратором.",
        );
      } else {
        setQueuesAccessError(
          "Не удалось загрузить список очередей. Попробуйте ещё раз.",
        );
      }
      refetchQueues();
      return;
    }

    // Обязательная валидация привязки филиала и очереди
    if (
      dropDownItem &&
      (selectedBranchIds.length === 0 || selectedQueueIds.length === 0)
    ) {
      setSelectedBranchesAndQueuesValidationError("Выберите филиалы и очереди");
      return;
    }
    if (isCreateMode && addedEmployees.length === 0) {
      setAddedEmployeesValidationError("Выберите минимум одного сотрудника");
      return;
    }

    const { workpaceName, boardNumber, digitalDisplayIp, officeNumber } = body;
    const normalizedWorkplaceName = (workpaceName ?? "").trim().toLowerCase();
    const duplicateExistsInSelectedBranches = allWorkingPlaces.some((place) => {
      if (!normalizedWorkplaceName || place.id === data?.id) return false;
      const placeName = (place.workingPlace ?? "").trim().toLowerCase();
      if (placeName !== normalizedWorkplaceName) return false;
      const placeBranchIds = place.branches?.map((branch) => branch.id) ?? [];
      return selectedBranchIds.some((branchId) =>
        placeBranchIds.includes(branchId),
      );
    });
    if (duplicateExistsInSelectedBranches) {
      setError("workpaceName", {
        type: "manual",
        message: "Такое наименование рабочего места уже существует",
      });
      return;
    }
    clearErrors("workpaceName");

    const normalizedBoardNumber = Number(boardNumber);
    const normalizedOfficeNumber = Number(officeNumber);
    const selectedQueueNames = selectedQueueIds
      .map((queueId) => availableQueues.find((q) => q.id === queueId)?.queueName)
      .filter((queueName): queueName is string => Boolean(queueName))
      .map((queueName) => queueName.trim().toLowerCase());

    const hasOfficeAndBoardNumbers =
      Number.isFinite(normalizedBoardNumber) &&
      normalizedBoardNumber > 0 &&
      Number.isFinite(normalizedOfficeNumber) &&
      normalizedOfficeNumber > 0;

    const duplicateOfficeAndBoardInQueue =
      hasOfficeAndBoardNumbers &&
      allWorkingPlaces.some((place) => {
        if (place.id === data?.id) return false;
        if (Number(place.number) !== normalizedBoardNumber) return false;
        if (Number(place.cabinet) !== normalizedOfficeNumber) return false;

        const placeQueueIds = place.queues?.map((queue) => queue.id) ?? [];
        const queueIdIntersection = selectedQueueIds.some((queueId) =>
          placeQueueIds.includes(queueId),
        );
        if (queueIdIntersection) return true;

        if (selectedQueueNames.length === 0) return false;
        const normalizedPlaceQueueName = (place.queueName ?? "")
          .trim()
          .toLowerCase();
        return selectedQueueNames.includes(normalizedPlaceQueueName);
      });

    if (duplicateOfficeAndBoardInQueue) {
      const duplicateComboError =
        "Для выбранной очереди комбинация номера окна и кабинета уже существует";
      setError("officeNumber", {
        type: "manual",
        message: duplicateComboError,
      });
      setError("boardNumber", {
        type: "manual",
        message: duplicateComboError,
      });
      return;
    }
    clearErrors("officeNumber");
    clearErrors("boardNumber");

    // Имя очереди для фильтра оператора: из единственной выбранной очереди в "Привязка к очередям"
    const queueName =
      selectedQueueIds.length === 1
        ? (availableQueues.find((q) => q.id === selectedQueueIds[0])
            ?.queueName ?? null)
        : null;
    const requestBody = {
      description: workpaceName,
      windowNumber: Number(boardNumber) || null,
      officeNumber: Number(officeNumber) || null,
      type: dropDownItem,
      assignedEmployees: addedEmployees,
      assignedEmployeesIds: addedEmployees?.map((item) => item.id),
      digitalDisplayIp,
      queueName: queueName?.trim() || null,
    };
    const requestOperatorsBody = {
      description: workpaceName,
      windowNumber: Number(boardNumber) || null,
      officeNumber: Number(officeNumber) || null,
      type: dropDownItem,
      users: addedEmployees?.map((item) => item.id),
      digitalDisplayIp,
      queueName: queueName?.trim() || null,
      ...(selectedBranchIds.length > 0 && { branchIds: selectedBranchIds }),
      ...(selectedQueueIds.length > 0 && { queueIds: selectedQueueIds }),
    };
    if (
      data &&
      (dropDownItem === "Оператор на въезд" ||
        dropDownItem === "Оператор на выезд")
    ) {
      editWorkingPlace({ requestBody: requestOperatorsBody, id: data.id });
      setBackStage(false);
    } else if (
      !data &&
      (dropDownItem === "Оператор на въезд" ||
        dropDownItem === "Оператор на выезд")
    ) {
      createWorkingPlace(requestOperatorsBody);
      setBackStage(false);
    }
    if (data && dropDownItem === "Оператор") {
      setAgentsRequestData({
        ...requestBody,
        id: data.id,
        queueName: queueName?.trim() || null,
        ...(selectedBranchIds.length > 0 && { branchIds: selectedBranchIds }),
        ...(selectedQueueIds.length > 0 && { queueIds: selectedQueueIds }),
      });
      setActive(false);
      setModalAccessLevelsActive(true);
      setBackStage(true);
    } else if (!data && dropDownItem === "Оператор") {
      setAgentsRequestData({
        ...requestBody,
        queueName: queueName?.trim() || null,
        ...(selectedBranchIds.length > 0 && { branchIds: selectedBranchIds }),
        ...(selectedQueueIds.length > 0 && { queueIds: selectedQueueIds }),
      });
      setActive(false);
      setModalAccessLevelsActive(true);
      setBackStage(true);
    }
  };

  const handleClickOutside = (event: MouseEvent): void => {
    if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
      setActive(false);
      setActiveRowData(null);
      setIsCreateMode(false);
      setBackStage(false);
    }
  };
  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const duplicateOfficeAndBoardError =
    errors.officeNumber?.type === "manual"
      ? errors.officeNumber?.message
      : errors.boardNumber?.type === "manual"
        ? errors.boardNumber?.message
        : null;

  return (
    <div
      className={classNames(modalBase.modal, { [modalBase.active]: isActive })}
    >
      <div
        className={classNames(modalBase.container, classes.modalContainer)}
        ref={modalRef}
      >
        <form className={classes.workplaceForm}>
          {(isLoadingEditWorkingPlace || isLoadingCreateWorkingPlace) && (
            <div className={classes.load}>
              <div className={classes.loadContent}>
                <p>Cохранение...</p>
              </div>
            </div>
          )}
          <h2 className={modalBase.title}>
            {isCreateMode
              ? "Создание рабочего места"
              : "Редактирование рабочего места"}
          </h2>
          <div className={modalBase.scrollArea}>
            {queuesAccessError && (
              <div style={{ marginBottom: 12, color: "#d92d20" }}>
                {queuesAccessError}
              </div>
            )}
            <div className={classes.workplaceContentGrid}>
              <div className={classes.card}>
                <h3 className={classes.sectionTitle}>Параметры</h3>
                <div className={classes.workpaceName}>
                  <CustomTextField
                    {...register("workpaceName")}
                    name="workpaceName"
                    value={watch("workpaceName")}
                    labelName={
                      isCreateMode
                        ? "Наименование рабочего места *"
                        : "Наименование рабочего места"
                    }
                    errorMessage={errors.workpaceName?.message}
                  />
                </div>
                <div className={classes.paramsRow}>
                  <div className={classes.accessLevel}>
                    <DropDown
                      isMenuOpen={isCreateMode ? false : isMenuOpen}
                      setMenuOpen={isCreateMode ? () => {} : setMenuOpen}
                      label={isCreateMode ? "Уровень доступа *" : "Уровень доступа"}
                      activeService={watch("type")}
                      isError={Boolean(errors.type)}
                    >
                      {uniqRoles &&
                        uniqRoles.map(
                          (item: {
                            id: number;
                            name: string;
                            role: string;
                          }) => (
                            <li
                              key={item.id}
                              className={classes.serviceItem}
                              onClick={(): void => {
                                setDropDownItem(item.role);
                                setAddedEmployees([]);
                                setMenuOpen(false);
                                setValue("type", item.role);
                              }}
                            >
                              <span>{item.role}</span>
                            </li>
                          ),
                        )}
                    </DropDown>
                    {errors.type?.message && (
                      <div className={classes.accessLevelError}>
                        {errors.type.message}
                      </div>
                    )}
                  </div>
                  {dropDownItem === "Оператор" && (
                    <div className={classes.officeNumber}>
                      <CustomTextField
                        {...register("officeNumber")}
                        name="officeNumber"
                        value={watch("officeNumber")}
                        labelName={
                          isCreateMode ? "Номер кабинета" : "Номер кабинета"
                        }
                        errorMessage={
                          errors.officeNumber?.type === "manual"
                            ? " "
                            : errors.officeNumber?.message
                        }
                      />
                      {duplicateOfficeAndBoardError && (
                        <div className={classes.accessLevelError}>
                          {duplicateOfficeAndBoardError}
                        </div>
                      )}
                    </div>
                  )}

                  {dropDownItem === "Оператор" && (
                    <div className={classes.windowNumber}>
                      <CustomTextField
                        {...register("boardNumber")}
                        name="boardNumber"
                        value={watch("boardNumber")}
                        labelName={
                          isCreateMode ? "Номер окна" : "Номер окна"
                        }
                        errorMessage={
                          errors.boardNumber?.type === "manual"
                            ? " "
                            : errors.officeNumber?.type === "required"
                              ? " "
                              : errors.boardNumber?.message
                        }
                      />
                    </div>
                  )}
                  {dropDownItem === "Оператор" && (
                    <div className={classes.windowIP}>
                      <CustomTextField
                        {...register("digitalDisplayIp")}
                        name="digitalDisplayIp"
                        value={watch("digitalDisplayIp")}
                        labelName={"IP табло"}
                        errorMessage={errors.digitalDisplayIp?.message}
                      />
                    </div>
                  )}
                </div>
              </div>

              <div className={classes.card}>
                <h3 className={classes.sectionTitle}>
                  {isCreateMode
                    ? "Привязка к филиалам и очередям (минимум одна очередь) *"
                    : "Привязка"}
                </h3>
                {dropDownItem ? (
                  <BranchQueueTreeSelect
                    selectedBranchIds={selectedBranchIds}
                    selectedQueueIds={selectedQueueIds}
                    onBranchChange={(ids) => {
                      setSelectedBranchIds(ids);
                      if (ids.length > 0 && selectedQueueIds.length > 0) {
                        setSelectedBranchesAndQueuesValidationError(null);
                      }
                    }}
                    onQueueChange={(ids) => {
                      setSelectedQueueIds(ids);
                      if (selectedBranchIds.length > 0 && ids.length > 0) {
                        setSelectedBranchesAndQueuesValidationError(null);
                      }
                    }}
                    className={classes.queuesBlock}
                    queueIdsFilter={queueIdsFilter}
                    errorMessage={selectedBranchesAndQueuesValidationError}
                  />
                ) : (
                  <div className={classes.bindingHint}>
                    Выберите уровень доступа, чтобы настроить привязку к
                    филиалам и очередям.
                  </div>
                )}
              </div>

              <div className={classes.workplaceEmployeesRow}>
                <div
                  className={classNames(classes.card, classes.searchContainer)}
                >
                  <h3 className={classes.sectionTitle}>Добавить сотрудников</h3>
                  <div className={classes.cardBody}>
                    <SearchTextField
                      {...register("searchInputText")}
                      name="searchInputText"
                      placeholder={"Введите название"}
                      clearInput={(): void => {
                        setValue("searchInputText", "");
                        setSearchText("");
                      }}
                      onChange={(e) => setSearchText(e.target.value)}
                    >
                      {filteredItems
                        ?.filter((e) =>
                          e.name
                            .toLowerCase()
                            .includes(searchText.toLowerCase()),
                        )
                        .map((item) => (
                          <div
                            key={item.id}
                            className={classes.userList}
                            onClick={() => {
                              addEmloyeeToArr(item);
                              removeSelectedUser(item);
                            }}
                          >
                            {item.name}
                          </div>
                        ))}
                    </SearchTextField>
                  </div>
                </div>
                <div
                  className={classNames(
                    classes.card,
                    classes.addedEmployees,
                    {
                      [classes.addedEmployeesWithError]: Boolean(
                        addedEmployeesValidationError,
                      ),
                    },
                  )}
                >
                  <h3 className={classes.sectionTitle}>
                    {isCreateMode
                      ? "Добавленные сотрудники *"
                      : "Добавленные сотрудники"}
                  </h3>
                  <div className={classes.cardBody}>
                    <CustomTextField
                      // labelName={"Добавленные сотрудники"}
                      disabled={true}
                      errorMessage={addedEmployeesValidationError ? " " : undefined}
                    >
                      {addedEmployees?.map((item) => (
                        <div
                          key={item.id}
                          className={classes.addedServiceItem}
                          onClick={() => {
                            removeEmplyeeById(item.id);
                            addRemovedEmployee(item);
                          }}
                        >
                          <div className={classes.crossContainer}>
                            <img src={crossIcon} alt={"cross"} />
                          </div>
                          {item.name}
                        </div>
                      ))}
                    </CustomTextField>
                    {addedEmployeesValidationError && (
                      <div className={classes.accessLevelError}>
                        {addedEmployeesValidationError}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div
            className={classNames(modalBase.footer, classes.buttonsContainer)}
          >
            <Button
              btnStyle={"white"}
              content="отмена"
              onClick={() => {
                setActive(false);
                setActiveRowData(null);
                setIsCreateMode(false);
                setBackStage(false);
                setAgentsRequestData(null);
              }}
            />
            <Button
              content={dropDownItem === "Оператор" ? "далее" : "сохранить"}
              btnStyle={"blue"}
              onClick={handleSubmit(onSubmit)}
              isLoading={
                isLoadingEditWorkingPlace || isLoadingCreateWorkingPlace
              }
            />
          </div>
        </form>
      </div>
    </div>
  );
};
